"use client";

import { useTranslation } from "react-i18next";
import { ChevronsUpDownIcon, UserIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuthUser } from "@/hooks/use-auth-user";
import { cn } from "@/lib/utils";

// Demo identity used during development & presentations. In production these
// fields fall back to useAuthUser(); locally the deck-friendly Czech profile
// keeps the sidebar visually complete without a real user account.
const DEV_USER_NAME = "JUDr. Jana Nováková";
const DEV_USER_FIRM = "Nováková & partneři";
const DEV_USER_INITIALS = "JN";

const isDev = process.env.NODE_ENV === "development";

const AVATAR_GRADIENT =
  "linear-gradient(135deg, oklch(0.5 0.06 256), oklch(0.4 0.08 280))";

export function SidebarUser({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation();
  const auth = useAuthUser();
  const navigate = useNavigate();

  const name = isDev ? DEV_USER_NAME : auth.username;
  const firm = isDev ? DEV_USER_FIRM : null;
  const initials = isDev ? DEV_USER_INITIALS : auth.initial;

  return (
    <footer
      className={cn(
        "praetor-sidebar-footer border-border/60 border-t",
        collapsed ? "p-2.5 px-2" : "p-1.5",
      )}
    >
      <button
        type="button"
        aria-label={t("sidebar.settings")}
        onClick={() => navigate("/settings")}
        className={cn(
          "flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 transition-colors duration-120 hover:bg-muted/60",
          collapsed && "justify-center",
        )}
      >
        <div
          className="praetor-sidebar-avatar flex size-[30px] shrink-0 select-none items-center justify-center rounded-full font-semibold text-[12px] text-white"
          style={{ background: AVATAR_GRADIENT }}
        >
          {initials ?? <UserIcon className="size-4" />}
        </div>

        {!collapsed && (
          <>
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden text-left">
              {name && (
                <span className="praetor-sidebar-user-name truncate font-medium text-[13px] text-foreground">
                  {name}
                </span>
              )}
              {firm && (
                <span className="praetor-sidebar-user-firm truncate text-[11.5px] text-muted-foreground">
                  {firm}
                </span>
              )}
            </div>

            <ChevronsUpDownIcon
              className="size-4 shrink-0 text-muted-foreground"
              strokeWidth={1.7}
            />
          </>
        )}
      </button>
    </footer>
  );
}
